import { type ReactElement } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';

import type { AiLeagueTabConfig } from '@/constants/aiLeague/aiLeagueTypes';
import { getAiModelTabIndex, getMethodologyTabIndex } from '@/constants/aiLeague/aiLeagueTabs';
import type { AiModelId } from '@/constants/aiLeague/aiLeagueTypes';

function MobileNavTab(props: {
  label: string;
  active: boolean;
  onClick: () => void;
  rightIcon?: ReactElement;
  'aria-expanded'?: boolean;
}) {
  const { label, active, onClick, rightIcon, 'aria-expanded': ariaExpanded } = props;
  const activeColor = useColorModeValue('blue.500', 'blue.300');
  const inactiveColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Button
      variant='unstyled'
      flexShrink={0}
      display='flex'
      alignItems='center'
      justifyContent='center'
      minH='40px'
      px={2}
      py={2}
      fontSize='sm'
      fontWeight={active ? 'semibold' : 'medium'}
      color={active ? activeColor : inactiveColor}
      borderBottomWidth='2px'
      borderBottomColor={active ? activeColor : 'transparent'}
      borderRadius={0}
      _hover={{ color: activeColor }}
      onClick={onClick}
      aria-expanded={ariaExpanded}
    >
      {label}
      {rightIcon}
    </Button>
  );
}

export function AiLeagueMobileTabBar(props: {
  tabIndex: number;
  aiLeagueTab: AiLeagueTabConfig;
  modelTabs: AiLeagueTabConfig[];
  methodologyTab: AiLeagueTabConfig;
  modelsMenuOpen: boolean;
  onToggleModelsMenu: () => void;
  onSelectTab: (index: number) => void;
  onSelectModel: (modelId: AiModelId) => void;
}) {
  const {
    tabIndex,
    aiLeagueTab,
    modelTabs,
    methodologyTab,
    modelsMenuOpen,
    onToggleModelsMenu,
    onSelectTab,
    onSelectModel,
  } = props;

  const methodologyIndex = getMethodologyTabIndex();
  const isModelTab = tabIndex >= 1 && tabIndex < methodologyIndex;
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const listBg = useColorModeValue('gray.50', 'gray.700');
  const listHoverBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const listActiveBg = useColorModeValue('blue.50', 'whiteAlpha.200');
  const activeColor = useColorModeValue('blue.500', 'blue.300');

  return (
    <Box w='full' borderBottomWidth='1px' borderColor={borderColor}>
      <Flex align='flex-end' justify='space-between' w='full' gap={1}>
        <MobileNavTab
          label={aiLeagueTab.label}
          active={tabIndex === 0}
          onClick={() => onSelectTab(0)}
        />
        <MobileNavTab
          label='Models'
          active={isModelTab || modelsMenuOpen}
          onClick={onToggleModelsMenu}
          aria-expanded={modelsMenuOpen}
          rightIcon={
            <Icon
              as={ChevronDownIcon}
              boxSize={4}
              ml={1}
              transition='transform 0.2s'
              transform={modelsMenuOpen ? 'rotate(180deg)' : undefined}
            />
          }
        />
        <MobileNavTab
          label={methodologyTab.label}
          active={tabIndex === methodologyIndex}
          onClick={() => onSelectTab(methodologyIndex)}
        />
      </Flex>

      <Collapse in={modelsMenuOpen} animateOpacity>
        <Box bg={listBg} borderTopWidth='1px' borderColor={borderColor} py={1}>
          {modelTabs.map((tab) => {
            const modelIndex = getAiModelTabIndex(tab.id as AiModelId);
            const selected = tabIndex === modelIndex;
            return (
              <Button
                key={tab.id}
                variant='unstyled'
                display='block'
                w='full'
                textAlign='left'
                px={4}
                py={2.5}
                fontSize='sm'
                fontWeight={selected ? 'semibold' : 'normal'}
                color={selected ? activeColor : undefined}
                bg={selected ? listActiveBg : 'transparent'}
                borderRadius={0}
                _hover={{ bg: selected ? listActiveBg : listHoverBg }}
                onClick={() => onSelectModel(tab.id as AiModelId)}
              >
                {tab.label}
              </Button>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
